// SpreadsheetMappingPanel — confirmation UI shown after a .csv/.xls/.xlsx upload.
//
// Flow (matches the user's spec):
//   1. Auto-detect sheets + headers + best-guess column mapping (server-side).
//   2. Pre-fill dropdowns; show 5-row live preview.
//   3. User confirms (or adjusts) date / description / amount / type / category cols.
//   4. ☑ "Save this mapping for future uploads from this bank" (checked by default).
//   5. Import → backend reads ALL rows with that mapping, inserts transactions,
//      skips blank-amount rows, returns counts.
//
// Saved mappings auto-apply the next time a file with the same column-header
// fingerprint is uploaded — a green banner shows "Using saved mapping from ___".

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CheckCircle, X, AlertCircle, Loader2, Save } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const NONE = "__NONE__";

export default function SpreadsheetMappingPanel({ docId, fileName, fromVault = false, onImported, onCancel }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sheets, setSheets] = useState([]);
  const [sheetName, setSheetName] = useState("");
  const [mapping, setMapping] = useState({});
  const [savedMapping, setSavedMapping] = useState(null);
  const [savedMappingLabel, setSavedMappingLabel] = useState("");
  const [saveMapping, setSaveMapping] = useState(true);
  const [importing, setImporting] = useState(false);

  // Load preview on mount
  useEffect(() => {
    if (!docId) return;
    setLoading(true);
    setError("");
    axios.get(`${API}/documents/${docId}/spreadsheet-preview`, { withCredentials: true })
      .then(({ data }) => {
        setSheets(data.sheets || []);
        const first = data.sheets?.[0];
        setSheetName(first?.name || "");
        // Saved mapping takes priority over auto-mapping
        if (data.saved_mapping) {
          setSavedMapping(data.saved_mapping);
          setSavedMappingLabel(data.saved_mapping_label || "");
          setMapping(data.saved_mapping);
        } else {
          setMapping(data.auto_mapping || {});
        }
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        const msg = typeof detail === "string"
          ? detail
          : (detail?.message || "Could not read spreadsheet — please check the file and try again.");
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [docId]);

  // When user switches sheets, recompute headers (mapping reset to auto for that sheet)
  const currentSheet = useMemo(
    () => sheets.find((s) => s.name === sheetName) || sheets[0],
    [sheets, sheetName]
  );
  const headers = currentSheet?.headers || [];
  const sampleRows = currentSheet?.sample_rows || [];

  // Live-preview which columns map to what
  const columnHighlight = useMemo(() => {
    const out = {};
    if (mapping.date_col) out[mapping.date_col] = "ring-2 ring-blue-300 bg-blue-50";
    if (mapping.description_col) out[mapping.description_col] = "ring-2 ring-purple-300 bg-purple-50";
    if (mapping.amount_col) out[mapping.amount_col] = "ring-2 ring-emerald-300 bg-emerald-50";
    if (mapping.debit_col) out[mapping.debit_col] = "ring-2 ring-rose-300 bg-rose-50";
    if (mapping.credit_col) out[mapping.credit_col] = "ring-2 ring-emerald-300 bg-emerald-50";
    return out;
  }, [mapping]);

  const setMap = (key, val) => setMapping((m) => ({ ...m, [key]: val === NONE ? null : val }));

  const canImport = useMemo(() => {
    return mapping.date_col
      && mapping.description_col
      && (mapping.amount_col || (mapping.debit_col && mapping.credit_col));
  }, [mapping]);

  const handleImport = async () => {
    setImporting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/documents/${docId}/spreadsheet-import`, {
        sheet_name: sheetName,
        mapping,
        save_mapping: saveMapping,
        label: savedMappingLabel || fileName,
      }, { withCredentials: true });
      onImported && onImported(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message || "Import failed — please check your column mapping and try again.");
      setError(msg);
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex items-center gap-3" data-testid="spreadsheet-panel-loading">
        <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
        <span className="text-slate-700">Reading spreadsheet…</span>
      </div>
    );
  }

  if (error && !sheets.length) {
    const isOversized = /row limit|column limit|cells across sheets/i.test(error);
    const reason = isOversized
      ? "This workbook is too large to process. Split it into smaller files or export it to CSV, then try again."
      : error;
    const trailer = fromVault
      ? "No Tax App document was created. Your original remains in the Vault."
      : "No Tax App document was created. Please correct the file and try again.";
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6" data-testid="spreadsheet-panel-error">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-bold text-red-800">Could not import this spreadsheet.</h3>
            <p className="text-red-700 text-sm mt-1">
              {reason} {trailer}
            </p>
          </div>
          <button onClick={onCancel} className="text-red-700 hover:text-red-900">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-5" data-testid="spreadsheet-mapping-panel">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-bold text-slate-900 text-lg">📊 Confirm Column Mapping</h3>
          <p className="text-slate-500 text-sm mt-1">{fileName}</p>
        </div>
        <button onClick={onCancel} data-testid="spreadsheet-cancel-btn" className="text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
      </div>

      {savedMapping && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-2" data-testid="spreadsheet-saved-mapping-banner">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <span className="text-green-800 text-sm">
            ✓ Using saved mapping{savedMappingLabel ? ` from "${savedMappingLabel}"` : ""}. Adjust below if needed.
          </span>
        </div>
      )}

      {sheets.length > 1 && (
        <div>
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1 block">Sheet</label>
          <select
            value={sheetName}
            onChange={(e) => setSheetName(e.target.value)}
            data-testid="spreadsheet-sheet-select"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
          >
            {sheets.map((s) => (
              <option key={s.name} value={s.name}>{s.name} ({s.row_count} rows)</option>
            ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MappingDropdown label="Date column" value={mapping.date_col} headers={headers} onChange={(v) => setMap("date_col", v)} required testid="map-date" />
        <MappingDropdown label="Description column" value={mapping.description_col} headers={headers} onChange={(v) => setMap("description_col", v)} required testid="map-description" />
        <MappingDropdown label="Amount column (single)" value={mapping.amount_col} headers={headers} onChange={(v) => setMap("amount_col", v)} testid="map-amount" />
        <div className="grid grid-cols-2 gap-2">
          <MappingDropdown label="Debit column" value={mapping.debit_col} headers={headers} onChange={(v) => setMap("debit_col", v)} testid="map-debit" />
          <MappingDropdown label="Credit column" value={mapping.credit_col} headers={headers} onChange={(v) => setMap("credit_col", v)} testid="map-credit" />
        </div>
        <MappingDropdown label="Type (income/expense)" value={mapping.type_col} headers={headers} onChange={(v) => setMap("type_col", v)} testid="map-type" />
        <MappingDropdown label="Category (optional)" value={mapping.category_col} headers={headers} onChange={(v) => setMap("category_col", v)} testid="map-category" />
      </div>

      <p className="text-xs text-slate-500">
        Either fill <span className="font-semibold">Amount column</span> OR fill both <span className="font-semibold">Debit + Credit</span>. Rows with blank/zero amounts are skipped.
      </p>

      {/* Live 5-row preview */}
      {sampleRows.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Preview (first {sampleRows.length} rows)</div>
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="min-w-full text-xs" data-testid="spreadsheet-preview-table">
              <thead className="bg-slate-50">
                <tr>
                  {headers.map((h) => (
                    <th key={h} className={`px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap ${columnHighlight[h] || ""}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sampleRows.map((row, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    {row.map((cell, j) => (
                      <td key={j} className={`px-3 py-2 text-slate-700 whitespace-nowrap ${columnHighlight[headers[j]] || ""}`}>{cell || <span className="text-slate-300">—</span>}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 pt-2 border-t border-slate-100">
        <label className="flex items-center gap-2 text-sm text-slate-700 select-none cursor-pointer" data-testid="spreadsheet-save-mapping-checkbox">
          <input
            type="checkbox"
            checked={saveMapping}
            onChange={(e) => setSaveMapping(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-300"
          />
          <Save className="w-4 h-4 text-slate-500" />
          Save this mapping for future uploads from this bank
        </label>
        <div className="flex items-center gap-2">
          <button onClick={onCancel} disabled={importing} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium">
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!canImport || importing}
            data-testid="spreadsheet-import-btn"
            className={`px-5 py-2 rounded-lg text-sm font-semibold text-white transition-all flex items-center gap-2 ${canImport && !importing ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-300 cursor-not-allowed"}`}
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            {importing ? "Importing…" : `Import ${currentSheet?.row_count || 0} rows`}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg" data-testid="spreadsheet-error">
          {error}
        </div>
      )}
    </div>
  );
}

function MappingDropdown({ label, value, headers, onChange, required, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1 block">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        value={value || NONE}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
      >
        <option value={NONE}>— None —</option>
        {headers.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
    </div>
  );
}
